import { defaultAxiosInstance } from '../config/axiosConfig';

/**
 * MCP Server Builder (`/mcp-servers`, permission `mcp_servers`).
 *
 * This is the PRODUCER side of MCP — turning a customer's own HTTP API, an
 * existing DIY agent, or KB folders into a hosted MCP server that Claude,
 * Cursor and the Anthropic/OpenAI APIs can call. Not to be confused with
 * customToolsService.fetchMcpTools(), which is the CONSUMER side (pointing at
 * somebody else's MCP server and importing its tools).
 *
 * Lifecycle:
 *   create (draft) → import spec → curate tools → test → enrich → lint → publish
 *                                                                        ↓
 *                                                          mint token + install
 *
 * A server is not publicly reachable until it is published. Draft, disabled and
 * deleted slugs all 404 on the public endpoint, deliberately — an anonymous
 * prober must not be able to enumerate which servers exist. The same reasoning
 * applies here: a 404 from these endpoints means "not found OR not yours", and
 * the UI must not imply the server exists.
 *
 * Auth is the caller's x-api-key, attached by the shared axios interceptor.
 * Errors are already normalised by the response interceptor into an Error
 * carrying `.status` and `.data` — so no try/catch here.
 */

// The shared instance times out at 30s. Importing a spec by URL and LLM
// enrichment both routinely run longer, and a bare timeout would surface as a
// failure when the work is actually still in flight server-side.
const IMPORT_TIMEOUT_MS = 120000;
const TEST_TIMEOUT_MS = 60000;
const ENRICH_TIMEOUT_MS = 180000;
// Lint and publish make an outbound request of their own on an OAuth server —
// they read the customer's JWKS URL — so they are no longer a local check.
const LINT_TIMEOUT_MS = 60000;

class McpServerService {
  constructor() {
    this.httpClient = defaultAxiosInstance;
  }

  // ── Servers ──────────────────────────────────────────────────────────────

  /** List the caller's MCP servers. @param {object} params limit | offset | status */
  async listServers(params = {}) {
    const res = await this.httpClient.get('/mcp-servers', { params });
    return res.data;
  }

  /**
   * Returns `oauth_config` as `{issuer, audience, jwks_uri, required_scopes[]}`
   * on a sign-in server, so the settings form prefills from it directly.
   *
   * The three Advanced fields are absent rather than masked. `client_secret` is
   * therefore indistinguishable from unset on read, which is why the form only
   * sends it when it was actually retyped — see `buildOauthConfigBody`.
   */
  async getServer(id) {
    const res = await this.httpClient.get(`/mcp-servers/${encodeURIComponent(id)}`);
    return res.data;
  }

  /**
   * @param {object} body { name, description, server_type: 'api'|'agent'|'knowledge',
   *   inbound_auth_mode: 'none'|'token'|'oauth', rate_limit_per_min,
   *   upstream_base_url?, upstream_auth_type?, upstream_api_key?,   // api
   *   credential_mode?: 'shared'|'per_user'|'per_user_url'|'per_user_oauth',
   *   oauth_config?,                                               // per_user_oauth
   *   diy_agent_id?,                                               // agent
   *   defer_after_seconds?, poll_after_seconds?, max_runtime_seconds?, // agent
   *   folder_ids?, is_dim3?                                        // knowledge
   * }
   *
   * The three `*_seconds` fields are agent-only (400 on any other type). In a
   * PATCH, 0 clears one (it reads back as `null`) and leaving it out keeps it.
   *
   * `credential_mode: 'per_user_oauth'` and `inbound_auth_mode: 'oauth'` are one
   * decision, not two — publishing is refused when they disagree, so always
   * send them together. `oauth_config` is { issuer, audience, jwks_uri,
   * required_scopes[], introspection_endpoint?, client_id?, client_secret? } and
   * every URL in it must be https.
   */
  async createServer(body) {
    const res = await this.httpClient.post('/mcp-servers', body);
    return res.data;
  }

  /** Partial update. Never send a masked secret (`****1234`) back as a real value. */
  async updateServer(id, body) {
    const res = await this.httpClient.patch(`/mcp-servers/${encodeURIComponent(id)}`, body);
    return res.data;
  }

  async deleteServer(id) {
    const res = await this.httpClient.delete(`/mcp-servers/${encodeURIComponent(id)}`);
    return res.data;
  }

  // ── Import ───────────────────────────────────────────────────────────────

  /**
   * Import an OpenAPI 3.x / Swagger 2.0 / Postman v2.1 / cURL spec.
   *
   * `source_type` should default to 'auto' in the UI — customers mislabel
   * formats constantly (a Postman export saved as openapi.json is the classic)
   * and the parser sniffs, corrects, and records a warning.
   *
   * @param {object} body { source_type, content } or { source_type, url }, plus replace_existing
   * @returns {Promise<{job_id, status, report, tools}>} report carries
   *   warnings[] and skipped[] — both must be surfaced, they explain a
   *   smaller-than-expected tool count and odd parameter names.
   */
  async importSpec(id, body) {
    const res = await this.httpClient.post(
      `/mcp-servers/${encodeURIComponent(id)}/import`,
      body,
      { timeout: IMPORT_TIMEOUT_MS }
    );
    return res.data;
  }

  // ── Tools / curation ─────────────────────────────────────────────────────

  /** All candidate tools with their `enabled` flag, annotations and confidence. */
  async listTools(id) {
    const res = await this.httpClient.get(`/mcp-servers/${encodeURIComponent(id)}/tools`);
    return res.data;
  }

  /**
   * Bulk enable/disable by name. Imports over ~40 candidates come back with
   * NONE enabled — the customer picks, and this is how the picking is saved.
   */
  async setToolsEnabled(id, toolNames, enabled) {
    const res = await this.httpClient.post(`/mcp-servers/${encodeURIComponent(id)}/tools/enable`, {
      tool_names: toolNames,
      enabled,
    });
    return res.data;
  }

  /** Edit one tool: tool_name, description, input_schema, annotations, enabled, order_index. */
  async updateTool(id, toolId, patch) {
    const res = await this.httpClient.patch(
      `/mcp-servers/${encodeURIComponent(id)}/tools/${encodeURIComponent(toolId)}`,
      patch
    );
    return res.data;
  }

  /**
   * Run the real upstream call through the exact path a live MCP call takes —
   * a green result means the credential, URL and schema all actually work.
   * The owner DOES see the real upstream error here; it's their own API.
   *
   * @param {string} [credential] required for `per_user` servers, which store
   *   no key of their own — sent for this call only and never persisted.
   *   Omitting it on a per_user server returns 400.
   */
  async testTool(id, toolId, args, credential) {
    const body = { arguments: args };
    if (credential) body.credential = credential;
    const res = await this.httpClient.post(
      `/mcp-servers/${encodeURIComponent(id)}/tools/${encodeURIComponent(toolId)}/test`,
      body,
      { timeout: TEST_TIMEOUT_MS }
    );
    return res.data;
  }

  // ── Enrichment ───────────────────────────────────────────────────────────

  /**
   * Propose better tool/parameter descriptions. Returns a DIFF and writes
   * nothing — applying is a separate, per-row decision. Costs LLM tokens on the
   * customer's own key and shows in their usage analytics as `mcp_generation`.
   */
  async enrich(id) {
    const res = await this.httpClient.post(
      `/mcp-servers/${encodeURIComponent(id)}/enrich`,
      {},
      { timeout: ENRICH_TIMEOUT_MS }
    );
    return res.data;
  }

  /** @param {Array} accepted the chosen entries from enrich()'s `changes` */
  async applyEnrichment(id, accepted) {
    const res = await this.httpClient.post(`/mcp-servers/${encodeURIComponent(id)}/enrich/apply`, {
      accepted,
    });
    return res.data;
  }

  // ── Lint / publish ───────────────────────────────────────────────────────

  /**
   * @returns {Promise<{blocking: string[], warnings: string[]}>} On an OAuth
   *   server the blocking findings include a real fetch of the JWKS URL, so a
   *   typo surfaces here instead of 401-ing every call after going live.
   */
  async lint(id) {
    const res = await this.httpClient.get(`/mcp-servers/${encodeURIComponent(id)}/lint`, {
      timeout: LINT_TIMEOUT_MS,
    });
    return res.data;
  }

  /** Re-lints server-side and refuses (400) on any blocking finding. */
  async publish(id) {
    const res = await this.httpClient.post(
      `/mcp-servers/${encodeURIComponent(id)}/publish`,
      {},
      { timeout: LINT_TIMEOUT_MS }
    );
    return res.data;
  }

  /** Takes it offline immediately — the public endpoint 404s within seconds. */
  async unpublish(id) {
    const res = await this.httpClient.post(`/mcp-servers/${encodeURIComponent(id)}/unpublish`, {});
    return res.data;
  }

  // ── Tokens / install ─────────────────────────────────────────────────────

  /**
   * Ready-to-paste config per client (Claude, Cursor, the APIs, curl).
   *
   * On a sign-in server this comes back with `uses_oauth: true` and
   * `oauth_issuer`, and both `requires_token` and `requires_user_key` false —
   * there is no token, header or `?key=` for anyone to copy, only the URL.
   */
  async getInstall(id) {
    const res = await this.httpClient.get(`/mcp-servers/${encodeURIComponent(id)}/install`);
    return res.data;
  }

  /**
   * Mint an inbound access token.
   *
   * The plaintext comes back EXACTLY ONCE and is not recoverable — there is no
   * retrieval endpoint, by design. Show it in a copy-to-clipboard modal that
   * requires explicit dismissal, and never try to re-fetch it.
   */
  async mintToken(id, body = {}) {
    const res = await this.httpClient.post(`/mcp-servers/${encodeURIComponent(id)}/tokens`, body);
    return res.data;
  }

  /** Metadata only — last4, expires_at, revoked_at, last_used_at. Never the token. */
  async listTokens(id) {
    const res = await this.httpClient.get(`/mcp-servers/${encodeURIComponent(id)}/tokens`);
    return res.data;
  }

  async revokeToken(id, tokenId) {
    const res = await this.httpClient.delete(
      `/mcp-servers/${encodeURIComponent(id)}/tokens/${encodeURIComponent(tokenId)}`
    );
    return res.data;
  }

  // ── Logs ─────────────────────────────────────────────────────────────────

  /**
   * Call log. Retention is MCP_CALL_LOG_RETENTION_DAYS (default 30), so an
   * empty result does NOT mean the server was never called.
   *
   * A deferred agent call is two rows — `deferred` (the app got a job ID) then
   * `get_job_status` → `ok` — plus an `error` row with the same `reference` if
   * the background run fails. `cancelled` means the app stopped waiting.
   * @param {object} params limit | offset | status
   */
  async listLogs(id, params = {}) {
    const res = await this.httpClient.get(`/mcp-servers/${encodeURIComponent(id)}/logs`, { params });
    return res.data;
  }
}

const mcpServerService = new McpServerService();

export default mcpServerService;
